import { useState, useEffect } from 'react'
import { Loader2, Save, Plus, X, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react'
import { useSettings, useUpdateSettings, useTestSdp, useSyncSdpSeq } from '@/hooks/useNaiDesk'

export default function SettingsAdmin() {
  const { data, isLoading } = useSettings()
  const update = useUpdateSettings()
  const test = useTestSdp()
  const sync = useSyncSdpSeq()

  const [draft, setDraft] = useState<any>(null)
  const [sdpClientId, setSdpClientId] = useState('')
  const [sdpClientSecret, setSdpClientSecret] = useState('')
  const [sdpRefreshToken, setSdpRefreshToken] = useState('')

  useEffect(() => {
    if (data && !draft) setDraft({ ...data })
  }, [data])

  if (isLoading || !draft) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>

  const save = () => {
    const payload: any = { ...draft }
    if (sdpClientId) payload.sdp_client_id = sdpClientId
    if (sdpClientSecret) payload.sdp_client_secret = sdpClientSecret
    if (sdpRefreshToken) payload.sdp_refresh_token = sdpRefreshToken
    update.mutate(payload, {
      onSuccess: () => {
        setSdpClientId(''); setSdpClientSecret(''); setSdpRefreshToken('')
      }
    })
  }

  const upd = (key: string, v: any) => setDraft({ ...draft, [key]: v })
  const updList = (key: string, v: string) => upd(key, v.split('\n').map(s => s.trim()).filter(Boolean))

  const subtypes = draft.ticket_subtypes || []
  const modules = draft.ticket_modules || []
  const matrix = draft.recipient_matrix || {}

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Configurações Nai Desk</h1>
          <p className="text-sm text-gray-500">E-mails, catálogos, matriz de destinatários, SDP, aparência</p>
        </div>
        <button onClick={save} disabled={update.isPending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">
          {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salvar
        </button>
      </header>

      <Section title="E-mails de notificação">
        <Field label="Email do administrador (recebe novos cadastros)">
          <input value={draft.admin_email || ''} onChange={e => upd('admin_email', e.target.value)} className="input" type="email" />
        </Field>
        <Field label="Email de suporte (recebe novos chamados)">
          <input value={draft.support_email || ''} onChange={e => upd('support_email', e.target.value)} className="input" type="email" />
        </Field>
      </Section>

      <Section title="Catálogos">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Tipos de chamado (um por linha)">
            <textarea rows={6} value={(draft.ticket_types || []).join('\n')} onChange={e => updList('ticket_types', e.target.value)} className="input font-mono text-xs" />
          </Field>
          <Field label="Subtipos">
            <textarea rows={6} value={(draft.ticket_subtypes || []).join('\n')} onChange={e => updList('ticket_subtypes', e.target.value)} className="input font-mono text-xs" />
          </Field>
          <Field label="Módulos">
            <textarea rows={6} value={(draft.ticket_modules || []).join('\n')} onChange={e => updList('ticket_modules', e.target.value)} className="input font-mono text-xs" />
          </Field>
          <Field label="Tags">
            <textarea rows={6} value={(draft.ticket_tags || []).join('\n')} onChange={e => updList('ticket_tags', e.target.value)} className="input font-mono text-xs" />
          </Field>
        </div>
      </Section>

      <Section title="Matriz de Destinatários por Subtipo × Módulo">
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="px-2 py-2 text-left font-semibold">Subtipo \\ Módulo</th>
                {modules.map((m: string) => <th key={m} className="px-2 py-2 text-left font-semibold">{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {subtypes.map((st: string) => (
                <tr key={st} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="px-2 py-1 font-medium">{st}</td>
                  {modules.map((m: string) => (
                    <td key={m} className="px-1 py-1">
                      <input
                        value={matrix[st]?.[m] || ''}
                        onChange={e => {
                          const next = { ...matrix }
                          next[st] = { ...(next[st] || {}), [m]: e.target.value }
                          if (!e.target.value) delete next[st][m]
                          upd('recipient_matrix', next)
                        }}
                        placeholder="email@..."
                        className="w-40 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Integração ManageEngine ServiceDesk Plus (SDP)">
        <label className="flex items-center gap-2 mb-3">
          <input type="checkbox" checked={!!draft.sdp_enabled} onChange={e => upd('sdp_enabled', e.target.checked)} />
          <span className="text-sm">Habilitar integração SDP</span>
        </label>
        <Field label="URL base do SDP (ex: https://sdpondemand.manageengine.com)">
          <input value={draft.sdp_base_url || ''} onChange={e => upd('sdp_base_url', e.target.value)} className="input" />
        </Field>
        <p className="text-xs text-gray-500 mb-2">Credenciais OAuth (deixe em branco para manter as atuais — {draft.sdp_has_credentials ? 'configuradas' : 'não configuradas'}):</p>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Client ID"><input type="password" value={sdpClientId} onChange={e => setSdpClientId(e.target.value)} className="input" /></Field>
          <Field label="Client Secret"><input type="password" value={sdpClientSecret} onChange={e => setSdpClientSecret(e.target.value)} className="input" /></Field>
          <Field label="Refresh Token"><input type="password" value={sdpRefreshToken} onChange={e => setSdpRefreshToken(e.target.value)} className="input" /></Field>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={() => test.mutate()} disabled={test.isPending}
            className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white inline-flex items-center gap-1 disabled:opacity-50">
            {test.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}Testar conexão
          </button>
          <button onClick={() => sync.mutate()} disabled={sync.isPending}
            className="text-xs px-3 py-1.5 rounded bg-purple-600 text-white inline-flex items-center gap-1 disabled:opacity-50">
            {sync.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}Sincronizar numeração
          </button>
        </div>
        {test.data && (
          <div className={`mt-2 text-xs px-3 py-2 rounded inline-flex items-center gap-2 ${test.data.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {test.data.ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
            {test.data.message || test.data.error}{test.data.last_request_id ? ` · último #${test.data.last_request_id}` : ''}
          </div>
        )}
        {sync.data && (
          <div className="mt-2 text-xs px-3 py-2 rounded bg-emerald-50 text-emerald-700 inline-flex items-center gap-2">
            <CheckCircle2 className="h-3 w-3" />Numeração sincronizada · próximo ticket: #{sync.data.seed}
          </div>
        )}

        <Field label="Mapeamento de status SDP → Portal (JSON)">
          <textarea
            rows={5}
            value={JSON.stringify(draft.sdp_status_map || {}, null, 2)}
            onChange={e => { try { upd('sdp_status_map', JSON.parse(e.target.value)) } catch { /* */ } }}
            className="input font-mono text-xs"
          />
        </Field>
        {draft.sdp_last_seen_request_id && <p className="text-xs text-gray-500">Último request SDP visto: #{draft.sdp_last_seen_request_id}</p>}
      </Section>

      <Section title="Aparência">
        <Field label="Logo URL"><input value={draft.portal_logo_url || ''} onChange={e => upd('portal_logo_url', e.target.value)} className="input" /></Field>
        <Field label="Imagem de fundo URL"><input value={draft.portal_bg_image_url || ''} onChange={e => upd('portal_bg_image_url', e.target.value)} className="input" /></Field>
        <Field label="Cor primária (hex)"><input type="color" value={draft.portal_primary_color || '#0ea5e9'} onChange={e => upd('portal_primary_color', e.target.value)} className="h-10 w-20" /></Field>
        <label className="flex items-center gap-2 text-sm mt-2">
          <input type="checkbox" checked={!!draft.portal_dark_mode_default} onChange={e => upd('portal_dark_mode_default', e.target.checked)} />
          Dark mode como padrão no portal
        </label>
      </Section>

      <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border-radius: 0.5rem; border: 1px solid rgb(229 231 235); background: white; font-size: 0.875rem; } .dark .input { border-color: rgb(55 65 81); background: rgb(31 41 55); color: white; }`}</style>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
      <h2 className="font-semibold mb-4 text-gray-900 dark:text-white">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  )
}
